<template>
    <button 
        :id="idElement"
        ref="btnProgram" 
        class="guide-grid-program" 
        :class="{'guide-grid-program_old': isOld}" 
        type="button" 
        :style="varStyleProgram" 
        :config-program="configProgram"
        @focus="onFocusProgram"
        @blur="onBlurProgram"
        @click="onClickProgram"
        @mouseenter="onHoverProgram"
    >
        <div class="guide-grid-program__inner" :class="{'guide-grid-program__inner_right': isPosRight}">
            <div class="guide-grid-program-name">{{ programName }}</div>
            <div class="guide-grid-program-time">{{ programTime }}</div>
        </div>
    </button>
</template>

<script setup lang="ts">
    import { type PropType ,onMounted ,onBeforeUnmount, type Ref, ref, computed, nextTick } from "vue";
    import type { ProgramGuideItem } from '@ca.vmedia.tv/domains';
    import {TimeUtils} from "@/utils";
    import {linkItems} from "@/components/Header/types";
    import { useRoute, useRouter } from 'vue-router';
    import { useGuideStore } from "@/stores/Guide";
    import { storeToRefs } from 'pinia';
    import { useI18n } from "vue-i18n";

    const GuideStoreModule = useGuideStore();
    const {
        addOffsetFocusedProgram,
        addOffsetFirstFocusedProgram,
        addOffsetLastFocusedProgram,
        setFocusedProgram,
        setCoordProgram,
    } = GuideStoreModule;
    const {
        timeMsPerPixel: timePerPixel,
        gapGuideGrid,
        channelWidth: channelWidthNumber,
        timelineBarTimes,
        timeMarkInterval
    } = storeToRefs(GuideStoreModule)

    interface Query {
        channelId: string;
        channelIdx: string;
        startime: string;
        [key: string]: string;
    }

    const props = defineProps({
        program: {
            type: Object as PropType<ProgramGuideItem>,
            required: true,
        },
        channelIdx: {
            type: Number,
            required: true,
        },
        itemsLength: {
            type: Number,
            required: true,
        },
        programIdx: {
            type: Number,
            required: true,
        },
        refGrid: {
            type: (null as unknown) as PropType<HTMLElement | null>,
            required: true,
        }
    });

    const { locale } = useI18n();
    const emit = defineEmits(['click-program']);
    const { $navigator } = useNuxtApp();
    const router = useRouter();
    const route = useRoute();
    const {guideConfig, isTimeFormat12} = useAppConfig();
    const featureMoveLeftRightEnabled = guideConfig.featureMoveLeftRightEnabled;
    const observer: Ref<IntersectionObserver | null> =  ref(null);
    const widthProgramContent: Ref<string> =  ref('100%');
    const isPosRight: Ref<boolean> =  ref(false);
    const offsetRight: Ref<string> =  ref('0px');
    const isHalfPrevProgram: Ref<boolean> =  ref(false);
    const btnProgram: Ref<HTMLButtonElement | null> =  ref(null);

    const isOld = computed((): boolean => {
        if (props.program && props.program.recentAirTime && props.program.recentAirTime.startTime && props.program.lengthInS) {
            const endTime = props.program.recentAirTime.startTime + props.program.lengthInS * 1000;

            return endTime < TimeUtils.dateNow() ? true : false;
        } else {
            return false;
        }
    });
    const isFirstElemnt = computed((): boolean => props.programIdx === 0);
    const canRegKeyRightListeners = computed((): boolean => {
        return (props.programIdx === props.itemsLength - 1) || (props.program?.recentAirTime?.startTime  && props.program?.lengthInS
        ? props.program.recentAirTime.startTime + props.program.lengthInS * 1000  > timelineBarTimes.value[timelineBarTimes.value.length - 1] + (timeMarkInterval.value / 2)
        : false);
    });
    const isOffsetEnable = computed((): boolean => (!isHalfPrevProgram.value && featureMoveLeftRightEnabled) || canRegKeyRightListeners.value);
    const idElement = computed((): string => `${props.channelIdx}_${props.program.recentAirTime?.startTime}`);
    const configProgram = computed((): number[] => {
        if (props.program && props.program.recentAirTime && props.program.recentAirTime.startTime) {
            return [props.channelIdx, props.program.recentAirTime?.startTime];
        } else {
            return [];
        }
    });
    const varStyleProgram = computed((): {[key: string]: string} => {
        return {
            '--width-program-content': widthProgramContent.value, 
            '--width-program': programWidth.value,
            '--offset-right-program': offsetRight.value
        };
    });
    const channelWidth = computed((): string => {
        return `${Math.floor(channelWidthNumber.value)}px`;
    });
    const programWidth = computed((): string => {
        let width = 0;
        if (props.program && props.program.lengthInS) {
            width = props.program.lengthInS * 1000 / timePerPixel.value - gapGuideGrid.value;
        }
        return `${Math.floor(width)}px`;
    });
    const programTime = computed((): string => {
        //The condition for empty programs
        if (props.program.id === null) {
            return '';
        }
        if (props.program && props.program.recentAirTime && props.program.recentAirTime.startTime) {
            const timeFormat = isTimeFormat12 ? 'hh:mm a' : 'HH:mm';
            return TimeUtils.getMoment(locale.value, props.program.recentAirTime.startTime).format(timeFormat).toUpperCase();
        }
        return '';
    });
    const programName= computed((): string => props.program?.shortName ?? props.program?.name ?? '');


    const onClickProgram = (): void => {
        emit('click-program');
    };
    const callbacksIntersectionObserver = (entries: IntersectionObserverEntry[]): void => {
        entries.forEach((entry: IntersectionObserverEntry) => {
                if (entry.boundingClientRect.x < entry.intersectionRect.x) {
                    isPosRight.value = true;
                    offsetRight.value = `${Math.floor(entry.boundingClientRect.right - entry.intersectionRect.right)}px`;

                    if (entry.intersectionRect.width > 0) {
                        isHalfPrevProgram.value = true;
                    }
                } 
                widthProgramContent.value = `${Math.floor(entry.intersectionRect.width)}px`;
        });
    };
    const startIntersectionObserver = (): void => {
        if (observer.value == null || btnProgram.value == null) {
            return;
        }
        
        observer.value.observe(btnProgram.value);
    };
    const disconnectIntersectionObserver = (): void => {
        if (observer.value == null) {
            return;
        }
        observer.value.disconnect();
    };
    const createIntersectionObserver = async () => {
        if (props.refGrid != null) {
                const options = {
                root: props.refGrid,
                rootMargin: `0px 0px 0px -${channelWidth.value}`
            };
            observer.value = new IntersectionObserver(callbacksIntersectionObserver, options);
        }
    };
    const onHoverProgram = (event: Event): void => {
        const btn = event.target as HTMLElement;
        btn.focus();
    };
    const addGetParams = (): void => {
        const channelId = props.program.recentAirTime?.channelId;
        const query: Query = {
            "channelId": `${channelId}`,
            "channelIdx": `${props.channelIdx}`,
            "startime": `${props.program.recentAirTime?.startTime}`
        };
        const routeQuery = route.query;
        const isQueryEqualRouteQuery = Object.keys(query).every(key => query[key] === routeQuery[key]);
        if (!isQueryEqualRouteQuery) {
            router.replace({ 
                path: linkItems.guide.routerPath, 
                query: query
            });
        }
    };
    const regKeyRightListeners = () => {
        $navigator.on("keypress:right", addOffsetForLastProgram);
    };
    const unregKeyRightListeners = () => {
        $navigator.off("keypress:right", addOffsetForLastProgram);
    };
    const regKeyLeftListeners = () => {
        $navigator.on("keypress:left", addOffsetForFirstProgram);
    };
    const unregKeyLeftListeners = () => {
        $navigator.off("keypress:left", addOffsetForFirstProgram);
    };
    const addOffsetForFirstProgram = (): void => {
        if (!featureMoveLeftRightEnabled) {
            return;
        };
        addOffsetFirstFocusedProgram({channelIdx: props.channelIdx, program: props.program})
    };
    const addOffsetForLastProgram = (): void => {
        if (!featureMoveLeftRightEnabled) {
            return;
        };
        addOffsetLastFocusedProgram({channelIdx: props.channelIdx, program: props.program});
    };
    const onBlurProgram = (): void => {
        unregKeyRightListeners();
        unregKeyLeftListeners();
    };
    const onFocusProgram = (): void => {
        addGetParams();

        setFocusedProgram(props.program);
        setCoordProgram(configProgram.value);

        if (canRegKeyRightListeners.value) {
            regKeyRightListeners();
        } else if (isOffsetEnable.value) {
            addOffsetFocusedProgram({program: props.program});

            if (isFirstElemnt.value) {
                regKeyLeftListeners();
            }
        }
    };

    onMounted(() => {
        nextTick(() => {
            createIntersectionObserver().then(() => {
                startIntersectionObserver();
            });
        });
    });

    onBeforeUnmount(() => {
        disconnectIntersectionObserver();
    });
</script>
